import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { api, facebookLogin, loadFacebookSdk } from './api'
import Button from './Button.jsx'

export default function Login({ config, onSignedIn, onForgotPassword }) {
  const { t } = useTranslation()
  const [mode, setMode] = useState('login')
  const [form, setForm] = useState({ email: '', password: '', displayName: '', businessName: '' })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  const facebook = config?.facebookLogin

  // Both halves default to open: the config only ever closes them, and a portal that hid the
  // email form because a fetch failed would leave the door shut with nobody able to knock.
  const emailRegistration = config?.emailSignIn?.registration !== false
  const emailSignIn = config?.emailSignIn?.signIn !== false

  const registering = mode === 'register' && emailRegistration
  const showEmail = registering ? emailRegistration : emailSignIn

  const set = (field) => (event) => setForm({ ...form, [field]: event.target.value })

  async function run(action) {
    setBusy(true)
    setError(null)
    try {
      onSignedIn(await action())
    } catch (failure) {
      // "cancelled" is someone closing the Facebook dialog, which is not worth shouting about.
      setError(failure.message === 'cancelled' ? null : failure.message)
    } finally {
      setBusy(false)
    }
  }

  const signInWithFacebook = () =>
    run(async () => {
      const FB = await loadFacebookSdk(facebook)
      return api.signInWithFacebook(await facebookLogin(FB, facebook.configId))
    })

  const submit = (event) => {
    event.preventDefault()
    return run(() =>
      registering
        ? api.register({
            email: form.email,
            password: form.password,
            displayName: form.displayName || null,
            businessName: form.businessName || null,
          })
        : api.login({ email: form.email, password: form.password }),
    )
  }

  return (
    <section className="card">
      <h2>{t(registering ? 'login.registerTitle' : 'login.title')}</h2>
      <p className="muted">{t('login.subtitle')}</p>

      {facebook?.enabled ? (
        <Button
          type="button"
          className="button button--facebook"
          loading={busy}
          onClick={signInWithFacebook}
        >
          {t('login.facebook')}
        </Button>
      ) : (
        <p className="muted small">{t('login.notConfigured')}</p>
      )}

      {showEmail && <p className="separator">{t('login.or')}</p>}

      {showEmail && (
      <form className="form" onSubmit={submit}>
        <label>
          {t('login.email')}
          <input type="email" required autoComplete="email" value={form.email} onChange={set('email')} />
        </label>

        <label>
          {t('login.password')}
          <input
            type="password"
            required
            minLength={registering ? 10 : undefined}
            autoComplete={registering ? 'new-password' : 'current-password'}
            value={form.password}
            onChange={set('password')}
          />
        </label>

        {registering && (
          <>
            <label>
              {t('login.displayName')}
              <input type="text" autoComplete="name" value={form.displayName} onChange={set('displayName')} />
            </label>

            <label>
              {t('login.businessName')}
              <input
                type="text"
                autoComplete="organization"
                value={form.businessName}
                onChange={set('businessName')}
              />
            </label>

            <p className="muted small">{t('login.passwordHint', { count: 10 })}</p>
          </>
        )}

        <Button type="submit" className="button" loading={busy}>
          {t(registering ? 'login.register' : 'login.signIn')}
        </Button>
      </form>
      )}

      {emailRegistration && emailSignIn && (
        <button
          type="button"
          className="link"
          onClick={() => {
            setMode(registering ? 'login' : 'register')
            setError(null)
          }}
        >
          {t(registering ? 'login.haveAccount' : 'login.needAccount')}
        </button>
      )}

      {showEmail && !registering && (
        <button type="button" className="link" onClick={onForgotPassword}>
          {t('login.forgotPassword')}
        </button>
      )}

      {error && <p className="status status--bad">{error}</p>}
    </section>
  )
}
